import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { Alert, Button, Icon, Loading, Modal, Typography } from "@/components/atoms";
import { AddCardForm } from "@/components/organisms/Onboarding/AddCardForm";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { StripePaymentMethod } from "@/redux/apis/payment/stripe/types";
import { useGetProviderPaymentMethodsQuery, useCreateSubscriptionMutation } from "@/redux/apis/subscription/subscriptionPlansApi";
import { SubscriptionPlan } from "@/redux/apis/subscription/types";
import { addToast } from "@/utils";
import { formatCurrency } from "@/utils/formatCurrency";

interface ChoosePlanPaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedPlan: SubscriptionPlan | null;
  onSuccess?: () => void;
}

// Helper to format card brand for display
const formatCardBrand = (brand: string): string => {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

interface PaymentMethodCardProps {
  paymentMethod: StripePaymentMethod;
  isSelected: boolean;
  onSelect: () => void;
}

const PaymentMethodCard = ({ paymentMethod, isSelected, onSelect }: PaymentMethodCardProps) => {
  const { card } = paymentMethod;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors ${
        isSelected ? "bg-primary/5 border-primary" : "border-neutral-light-gray hover:border-neutral-dark-gray"
      }`}
    >
      <div
        className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
          isSelected ? "border-primary" : "border-neutral-dark-gray"
        }`}
      >
        {isSelected && <div className="h-2.5 w-2.5 rounded-full bg-primary" />}
      </div>
      <Icon name="Card" size="lg" className="text-neutral-dark-gray" />
      <div className="flex flex-1 flex-col">
        <Typography variant="paragraph">
          {formatCardBrand(card.brand)} ending in {card.last4}
        </Typography>
        <Typography variant="small" className="text-neutral-dark-gray">
          Expires {card.expMonth.toString().padStart(2, "0")}/{card.expYear}
        </Typography>
      </div>
    </button>
  );
};

export const ChoosePlanPaymentModal = ({ open, onClose, selectedPlan, onSuccess }: ChoosePlanPaymentModalProps) => {
  const operator = useOperator();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    refetch,
  } = useGetProviderPaymentMethodsQuery({ providerId: operator.id });

  const [createSubscription, { isLoading: isCreating }] = useCreateSubscriptionMutation();

  const paymentMethods = paymentMethodsData || [];

  const handleCancel = () => {
    setSelectedPaymentMethodId(null);
    setShowAddCard(false);
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedPaymentMethodId) {
      setError("Please select a payment method");
      return;
    }

    if (!selectedPlan) {
      setError("No plan selected");
      return;
    }

    try {
      await createSubscription({
        providerId: operator.id,
        subscriptionPlanId: selectedPlan.subscriptionId,
        stripePaymentMethodId: selectedPaymentMethodId,
      }).unwrap();

      addToast("success", `Successfully subscribed to ${selectedPlan.subscriptionName}`);
      onSuccess?.();
      handleCancel();
      window.location.reload();
    } catch (err) {
      const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
      setError(errors[0] || "Failed to create subscription");
    }
  };

  const handleCardAdded = async (paymentMethodId: string) => {
    setSelectedPaymentMethodId(paymentMethodId);
    setShowAddCard(false);
    refetch();
  };

  const handleAddCardCancel = () => {
    setShowAddCard(false);
  };

  return (
    <Modal open={open} onClose={handleCancel} className="w-[85vw] md:w-[500px] lg:w-[500px]">
      <div className="flex flex-col">
        {/* Title and Description */}
        <div className="flex flex-col gap-2">
          <Typography variant="title">Choose Payment Method</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {showAddCard ? "Fill up the credit card information" : "Select a payment method for your new subscription"}
          </Typography>
        </div>

        {/* Selected Plan Summary */}
        {selectedPlan && !showAddCard && (
          <div className="mt-4 flex items-center justify-between rounded-lg bg-neutral-surface-gray p-4">
            <div className="flex items-center gap-3">
              {selectedPlan.iconSvg && (
                <div
                  className="h-10 w-10 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full"
                  dangerouslySetInnerHTML={{ __html: selectedPlan.iconSvg }}
                />
              )}
              <div className="flex flex-col">
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Selected Plan
                </Typography>
                <Typography variant="title">{selectedPlan.subscriptionName}</Typography>
              </div>
            </div>
            <Typography variant="title">{formatCurrency(selectedPlan.subscriptionFee, 0, "AUD")}/mo</Typography>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mt-4">
            <Alert type="danger" message={error} />
          </div>
        )}

        {/* Content Section */}
        <div className="flex flex-col gap-4 py-6">
          {isLoadingPaymentMethods ? (
            <div className="relative min-h-[150px]">
              <Loading className="rounded-lg" />
            </div>
          ) : showAddCard ? (
            <AddCardForm providerId={operator.id} countryCode={operator.country} onCardAdded={handleCardAdded} onCancel={handleAddCardCancel} />
          ) : (
            <>
              {/* Existing Payment Methods */}
              {paymentMethods.length > 0 && (
                <div className="flex flex-col gap-3">
                  {paymentMethods.map((pm) => (
                    <PaymentMethodCard
                      key={pm.paymentMethodId}
                      paymentMethod={pm}
                      isSelected={selectedPaymentMethodId === pm.paymentMethodId}
                      onSelect={() => setSelectedPaymentMethodId(pm.paymentMethodId)}
                    />
                  ))}
                </div>
              )}

              {/* Add New Card Button */}
              <button
                type="button"
                onClick={() => setShowAddCard(true)}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed border-neutral-dark-gray p-4 text-left transition-colors hover:bg-neutral-surface-gray"
              >
                <Icon name="Add" size="lg" className="text-primary" />
                <Typography variant="paragraph" className="text-primary">
                  Add new payment method
                </Typography>
              </button>
            </>
          )}
        </div>

        {/* Terms Text - Only show when not adding a card */}
        {!showAddCard && (
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            By confirming, you authorize RideMinder to charge this card for your subscription in accordance with their terms.
          </Typography>
        )}

        {/* Action Buttons - Only show when not adding a card (AddCardForm has its own buttons) */}
        {!showAddCard && (
          <div className="mt-8 flex flex-row justify-end gap-3">
            <Button type="button" size="lg" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" size="lg" variant="primary" onClick={handleConfirm} disabled={!selectedPaymentMethodId || isCreating}>
              {isCreating ? "Subscribing..." : "Confirm Subscription"}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
